import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { getSDK } from '../services/sdk-instance';
import { claimsAtom } from '../stores/auth/authState';
import type { PlatformFlagDefinition, PlatformFlagPublishEntry } from '@localagent/sdk';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

/**
 * Releases viewer: a READ-ONLY listing of
 * the published flag definitions — status, owner, target removal, last
 * publish. There are NO establish/edit/override controls; publishing is `la
 * release` through the internal trigger route only.
 *
 * `ReleasesViewer` is the presentational half the functional stories render
 * (props-driven); `ReleasesView` owns the fetch. The caller gates the route on
 * `is_platform_admin` from the session claims — the backend re-checks
 * platform-admin scope on every read (authz ADR-009).
 */

export interface ReleasesViewerProps {
  generation: string;
  flags: PlatformFlagDefinition[];
  publishHistory: PlatformFlagPublishEntry[];
  loading: boolean;
  error: string | null;
}

/** The last publish record for a key, from the document's history ring. */
export function lastPublishFor(
  history: PlatformFlagPublishEntry[],
  key: string,
): PlatformFlagPublishEntry | undefined {
  let last: PlatformFlagPublishEntry | undefined;
  for (const entry of history) {
    if (entry.flag_key === key) last = entry;
  }
  return last;
}

export function ReleasesViewer({
  generation,
  flags,
  publishHistory,
  loading,
  error,
}: ReleasesViewerProps) {
  return (
    <div className="space-y-4" data-testid="releases-view">
      <div>
        <h2 className="text-title-2 font-semibold">Releases</h2>
        <p className="text-label text-muted-foreground">
          Published feature flags — read-only. Authoritative history is the git
          log of the flags/ tree; publishing runs through <code>la release</code>.
        </p>
      </div>
      {error && (
        <p className="text-label text-destructive" role="alert" data-testid="releases-error">
          {error}
        </p>
      )}
      {loading && (
        <LoadingState data-testid="releases-loading" label="Loading published flags…" />
      )}
      {!loading && !error && (
        <div className="divide-y" data-testid="releases-table">
          <div className="flex items-center justify-between p-4 text-caption text-muted-foreground">
            <span>Generation</span>
            <code data-testid="releases-generation">{generation || 'none'}</code>
          </div>
          {flags.map((flag) => {
            const last = lastPublishFor(publishHistory, flag.key);
            return (
              <div
                key={flag.key}
                className="p-4 space-y-2"
                data-testid={`releases-row-${flag.key}`}
              >
                <div className="flex items-center gap-3">
                  <code className="font-medium">{flag.key}</code>
                  <span
                    className="rounded bg-muted px-2 py-0.5 text-caption"
                    data-testid={`releases-status-${flag.key}`}
                  >
                    {flag.status}
                  </span>
                  <span className="text-caption text-muted-foreground">{flag.scope}</span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-caption text-muted-foreground">
                  <span data-testid={`releases-owner-${flag.key}`}>owner: {flag.owner}</span>
                  <span data-testid={`releases-target-removal-${flag.key}`}>
                    target removal: {flag.target_removal_at}
                  </span>
                  <span data-testid={`releases-last-publish-${flag.key}`}>
                    last publish:{' '}
                    {last
                      ? `${last.action} by ${last.actor} at ${last.published_at}`
                      : 'never published'}
                  </span>
                </div>
              </div>
            );
          })}
          {flags.length === 0 && (
            <p className="p-4 text-body text-muted-foreground" data-testid="releases-empty">
              No flags published.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function ReleasesView() {
  const [state, setState] = useState<{
    generation: string;
    flags: PlatformFlagDefinition[];
    publishHistory: PlatformFlagPublishEntry[];
    loading: boolean;
    error: string | null;
  }>({
    generation: '',
    flags: [],
    publishHistory: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    getSDK()
      .flags.definitions()
      .then((doc) => {
        if (cancelled) return;
        setState({
          generation: doc.generation,
          flags: doc.flags,
          publishHistory: doc.publish_history,
          loading: false,
          error: null,
        });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          error: cause instanceof Error ? cause.message : 'Failed to load published flags',
        }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <ReleasesViewer {...state} />;
}

/**
 * Route gate: org admins and ordinary users never see flag state — the
 * page renders only for `is_platform_admin` claims. The backend refuses the
 * underlying read for anyone else regardless of what this renders.
 */
export function ReleasesRoute() {
  const claims = useAtomValue(claimsAtom);
  if (!claims?.isPlatformAdmin) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <h1 className="text-title-1 font-semibold">Releases</h1>
        <p className="text-body text-muted-foreground" data-testid="releases-forbidden">
          Releases are available to platform administrators only.
        </p>
      </div>
    );
  }
  return <ReleasesView />;
}
